/**
 * 管理ユーザーAPI
 */
import { Component, Vue } from 'vue-property-decorator'
import { ApiResponseManagerList, ApiResponseManagerEditData } from '@/models/apiResponses/managers'
import { TeacherSearchParamsType, TeacherEditType } from '@/models/teacherListModel'
import { UserRoles } from '@/const/UserRoles'

@Component
export default class ManagersApi extends Vue {
  /**
   * 管理ユーザー一覧取得
   * @param params 検索パラメーター群
   * @param role ユーザー権限
   * @returns ページネーション情報付き管理ユーザー一覧情報
   */
  protected async searchManagers(params: TeacherSearchParamsType, role: UserRoles): Promise<ApiResponseManagerList> {
    const { data }: { data: ApiResponseManagerList } = await Vue.prototype.$http.httpWithToken
      .get(`/v2/${role}/managers/search`, { params: params })
      .catch(() => ({ data: { responses: [], pagination: null } }))

    return data
  }

  /**
   * 管理ユーザー情報取得
   * @param id ユーザーID
   * @param role ユーザー権限
   * @returns 管理ユーザー情報
   */
  protected async getManagerData(id: number, role: UserRoles): Promise<ApiResponseManagerEditData> {
    const { data }: { data: ApiResponseManagerEditData } = await Vue.prototype.$http.httpWithToken.get(
      `/v2/${role}/managers/${id}`
    )

    return data
  }

  /**
   * 管理ユーザー情報登録
   * @param editData 管理ユーザー編集情報
   * @param role ユーザー権限
   */
  protected async createManagerData(editData: TeacherEditType, role: UserRoles): Promise<void> {
    await Vue.prototype.$http.httpWithToken.post(`/v2/${role}/managers`, {
      academyId: Number(editData.academyId),
      branchId: Number(editData.branchId),
      userName: editData.userName,
      bcbtCode: editData.bcbtCode,
      password: editData.password,
      passwordConfirm: editData.passwordConfirm,
      role: editData.role,
    })
  }

  /**
   * 管理ユーザー情報更新
   * @param editData 管理ユーザー編集情報
   * @param role ユーザー権限
   */
  protected async updateManagerData(editData: TeacherEditType, role: UserRoles): Promise<void> {
    await Vue.prototype.$http.httpWithToken.put(`/v2/${role}/managers/${editData.userId}`, {
      userName: editData.userName,
      password: editData.password,
      passwordConfirm: editData.passwordConfirm,
      role: editData.role,
    })
  }

  /**
   * 管理ユーザー情報削除
   * @param id ユーザーID
   * @param role ユーザー権限
   */
  protected async deleteManagerData(id: number, role: UserRoles): Promise<void> {
    await Vue.prototype.$http.httpWithToken.delete(`/v2/${role}/managers/${id}`)
  }

  /**
   * 管理者一覧のエクセルダウンロード
   * @param params 検索パラメーター群
   * @param userIds ダウンロード対象の管理ユーザーID
   * @param role ユーザー権限
   * @returns エクセルデータ
   */
  protected async exportManagerExcel(
    params: TeacherSearchParamsType,
    userIds: number[],
    role: UserRoles
  ): Promise<Blob> {
    const { data }: { data: Blob } = await Vue.prototype.$http.httpWithToken.get(`/v2/${role}/managers/export`, {
      params: Object.assign(params, { userIds: userIds }),
      responseType: 'blob',
      dataType: 'binary',
    })

    return data
  }

  /**
   * 管理者一覧エクセルアップロード
   * @param file エクセルファイル
   * @param role ユーザー権限
   */
  protected async importManagerExcel(file: File, role: UserRoles): Promise<void> {
    const headers = { 'Content-Type': 'multipart/form-data' }
    const params = new FormData()
    params.append('excel', file)
    await Vue.prototype.$http.httpWithToken.post(`/v2/${role}/managers/import`, params, {
      headers: headers,
    })
  }

  /**
   * 管理者QRログイン用PDFダウンロード
   * @param params 検索パラメーター群
   * @param userIds ダウンロード対象の管理ユーザーID
   * @param role ユーザー権限
   * @returns pdfデータ
   */
  protected async downloadManagerQrLoginPdf(
    params: TeacherSearchParamsType,
    userIds: number[],
    role: UserRoles
  ): Promise<Blob> {
    const { data }: { data: Blob } = await Vue.prototype.$http.httpWithToken.get(`/v2/${role}/managers/download_pdf`, {
      params: Object.assign(params, { userIds: userIds }),
      responseType: 'blob',
      dataType: 'binary',
    })

    return data
  }
}
